import { isFunction } from "../helpers/functional-util.js";
import { isNumber } from "@foretagsplatsen/core-helpers/number";
import { isRegExp } from "../helpers/string-util.js";
import materialize from "./materialize.js";

/**
 * @typedef ResponseHandler
 * @type {object}
 * @property {RegExp|number|function(ResponseContext):
 *   boolean|boolean} test - Decide if the response handler applies to
 *   the current response. If a RegExp, it should match the response
 *   status code. If a number, it should equal the response status
 *   code. If a function, it should return true if the response
 *   handler can handle the response context passed as parameter. If a
 *   boolean, the response handle will be used if the boolean is true.
 * @property {function(ResponseContext): Promise} handle - Do
 *   something with the response
 */

/**
 * `ResponseHandlerChain` is responsible for forwarding a response to
 * the most appropriate response handler. It maintains an ordered
 * sequence of {@link ResponseHandler} instances: the first one which
 * matches the response has to handle it.
 *
 * @param {ResponseHandler[]} [responseHandlers] - The ordered
 *   sequence of handlers. Default response handlers are always
 *   appended to this list to handle general-purpose response
 *   successes and failures
 */
export default class ResponseHandlerChain {
	constructor({ responseHandlers = [] } = {}) {
		this._responseHandlers = [
			...responseHandlers,
			...defaultResponseHandlers(),
		];
	}

	handle(responseContext) {
		const responseHandler = this._findHandler({ responseContext });

		return responseHandler.handle(responseContext);
	}

	_findHandler({ responseContext }) {
		return this._responseHandlers.find((responseHandler) =>
			this._isMatchingResponse({ responseHandler, responseContext }),
		);
	}

	_isMatchingResponse({ responseHandler: { test }, responseContext }) {
		if (isRegExp(test)) {
			return test.test(responseContext.response.status.toString());
		}

		if (isNumber(test)) {
			return test === responseContext.response.status;
		}

		if (isFunction(test)) {
			return test(responseContext);
		}

		return test === true;
	}
}

function defaultResponseHandlers() {
	return [
		{
			test: /^2../,
			handle: (...rest) => handleSuccess(...rest),
		},
		{
			test: true,
			handle: (...rest) => handleError(...rest),
		},
	];
}

async function handleSuccess({
	response,
	context,
	shouldReadResponse,
	successMessage,
	alertBus,
	materializer,
}) {
	let result = undefined;

	if (shouldReadResponse) {
		result = await readResponse({ response, context, materializer });
	}

	if (successMessage) {
		alertBus.success({ description: successMessage });
	}

	return result;
}

async function readResponse({ response, context, materializer }) {
	if (!response) {
		return response;
	}

	const responseText = await response.text();

	if (!responseText) {
		return responseText;
	}

	return materialize({
		data: JSON.parse(responseText),
		context,
		materializer,
	});
}

async function handleError({ response, errorMessage, alertBus }) {
	let responseJSON;
	const responseText = await response.text();

	try {
		responseJSON = JSON.parse(responseText);
	} catch {
		// keep responseJSON undefined if there was no JSON in the
		// response.
	}

	if (errorMessage) {
		const description = responseJSON?.message || responseText;

		alertBus.error({ description: `${errorMessage} : ${description}` });
	}

	// It would be much better to throw an instance of Error but this
	// is what the rest of the code expects:
	throw {
		responseJSON,
		responseText,
		status: response.status,
		statusText: response.statusText,
		isRepositoryError: true,
	};
}
